import React, { useMemo } from 'react';
import SourceSelector from '@/components/SourceSelector';
import { StickyFormBar } from '@/components/common';
import {
  AppAccordion,
  AppButton,
  AppFieldGrid,
  AppFieldGridSpan,
  AppFormField,
  AppInput,
  AppSelect,
  AppTextarea,
} from '@/ui';
import {
  getGroupedFields,
  getSearchCategoryDef,
  type SearchFilterFieldDef,
  type SearchFilterFieldGroup,
} from '@/utils/searchCategoryConfig';
import type { SearchAdminFormState } from '@/utils/searchAdminForm';
import type { Category, Source } from '@/types';
import SearchVisibilityPanel from '@/components/searches/SearchVisibilityPanel';
import VehicleCatalogField from '@/components/searches/VehicleCatalogField';

const GROUP_PANEL_IDS: Record<SearchFilterFieldGroup, string> = {
  keywords: 'keywords',
  location: 'location',
  property: 'property',
  details: 'details',
  advanced: 'advanced',
  pricing: 'price',
};

const GROUP_DEFAULT_EXPANDED: Partial<Record<SearchFilterFieldGroup, boolean>> = {
  keywords: true,
  details: true,
  property: true,
};

type Props = {
  storageScope: string;
  /** Remount form fields when switching create/edit/duplicate targets. */
  formKey: string;
  form: SearchAdminFormState;
  categories: Category[];
  sources: Source[];
  editId: string | null;
  isAdmin: boolean;
  onChange: (next: SearchAdminFormState) => void;
  onCategoryChange: (category: string) => void;
  onToggleSource: (name: string) => void;
  onSelectAllSources?: () => void;
  onClearAllSources?: () => void;
  sourceLimitHint?: string;
  showVisibilitySection?: boolean;
  showWorkspaceVisibilityOption?: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onCancelEdit?: () => void;
};

function renderField(
  field: SearchFilterFieldDef,
  form: SearchAdminFormState,
  onChange: (next: SearchAdminFormState) => void,
) {
  const value = form[field.key];
  const gridClass =
    field.gridClass === 'admin-form-grid__span-2'
      ? 2
      : field.gridClass === 'admin-form-grid__full'
        ? 'full'
        : undefined;

  const wrap = (node: React.ReactNode) =>
    gridClass ? (
      <AppFieldGridSpan span={gridClass as 2 | 'full'}>{node}</AppFieldGridSpan>
    ) : (
      node
    );

  if (field.type === 'textarea') {
    return wrap(
      <AppFormField
        key={field.key}
        label={field.label}
        helperText={field.hint}
      >
        <AppTextarea
          rows={3}
          value={String(value ?? '')}
          placeholder={field.placeholder}
          onChange={(e) => onChange({ ...form, [field.key]: e.target.value })}
        />
      </AppFormField>,
    );
  }

  if (field.type === 'select') {
    return wrap(
      <AppFormField key={field.key} label={field.label}>
        <AppSelect
          value={String(value ?? '')}
          onChange={(e) =>
            onChange({
              ...form,
              [field.key]: e.target.value as SearchAdminFormState[typeof field.key],
            })
          }
        >
          {(field.options ?? []).map((opt) => (
            <option key={opt.value || '__any__'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </AppSelect>
      </AppFormField>,
    );
  }

  if (field.type === 'catalog' && field.catalogKind && field.catalogIdKey) {
    return wrap(
      <VehicleCatalogField
        key={field.key}
        kind={field.catalogKind}
        label={field.label}
        value={String(value ?? '')}
        catalogId={String(form[field.catalogIdKey] ?? '')}
        parentMakeId={field.catalogKind !== 'make' ? form.vehicle_make_id || undefined : undefined}
        parentModelId={field.catalogKind === 'trim' ? form.vehicle_model_id || undefined : undefined}
        placeholder={field.placeholder}
        onChange={({ value: nextValue, catalogId }) => {
          const next = {
            ...form,
            [field.key]: nextValue,
            [field.catalogIdKey!]: catalogId,
          } as SearchAdminFormState;
          if (field.catalogKind === 'make') {
            next.model = '';
            next.trim = '';
            next.vehicle_model_id = '';
            next.vehicle_trim_id = '';
          } else if (field.catalogKind === 'model') {
            next.trim = '';
            next.vehicle_trim_id = '';
          }
          onChange(next);
        }}
      />,
    );
  }

  return wrap(
    <AppFormField key={field.key} label={field.label}>
      <AppInput
        type={field.type === 'number' ? 'number' : 'text'}
        value={String(value ?? '')}
        placeholder={field.placeholder}
        onChange={(e) => onChange({ ...form, [field.key]: e.target.value })}
      />
    </AppFormField>,
  );
}

export default function SearchAdminFormPanels({
  storageScope,
  formKey,
  form,
  categories,
  sources,
  editId,
  isAdmin,
  onChange,
  onCategoryChange,
  onToggleSource,
  onSelectAllSources,
  onClearAllSources,
  sourceLimitHint,
  showVisibilitySection = false,
  showWorkspaceVisibilityOption = true,
  onSubmit,
  onCancelEdit,
}: Props) {
  const categoryDef = getSearchCategoryDef(form.category);
  const grouped = useMemo(() => getGroupedFields(form.category), [form.category]);

  const { filterGroups, notificationFields } = useMemo(() => {
    const notification: SearchFilterFieldDef[] = [];
    const groups = grouped.map(({ group, label, fields }) => ({
      group,
      label,
      fields: fields.filter((field) => {
        if (field.key === 'message_templates') {
          notification.push(field);
          return false;
        }
        return true;
      }),
    }));
    return { filterGroups: groups.filter((g) => g.fields.length > 0), notificationFields: notification };
  }, [grouped]);

  const groupTitle = (group: SearchFilterFieldGroup, label: string): string => {
    if (group === 'details' && form.category === 'vehicles') return 'Vehicle details';
    if (group === 'advanced' && form.category === 'vehicles') return 'Advanced';
    if (group === 'property' && form.category === 'apartments') return 'Property';
    if (group === 'location') return 'Location';
    if (group === 'pricing') return 'Price';
    return label;
  };

  return (
    <AppAccordion
      key={editId ?? 'create'}
      id="search-form"
      storageScope={storageScope}
      title={editId ? 'Edit search' : 'Create search'}
      subtitle={editId ? form.name || 'Update filters and sources' : 'Define filters, category, and sources'}
      defaultExpanded={true}
    >
      <form key={formKey} className="search-admin-form" onSubmit={onSubmit}>
        <AppAccordion
          id="general"
          storageScope={storageScope}
          title="General"
          subtitle={editId ? 'Editing search' : 'Name and category'}
          defaultExpanded
        >
          <AppFieldGrid columns={4} className="admin-form-grid--general">
            <AppFieldGridSpan span={2}>
              <AppFormField label="Name" required>
                <AppInput
                  value={form.name}
                  onChange={(e) => onChange({ ...form, name: e.target.value })}
                  required
                />
              </AppFormField>
            </AppFieldGridSpan>
            <AppFormField label="Category">
              <AppSelect
                value={form.category}
                onChange={(e) => onCategoryChange(e.target.value)}
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </AppSelect>
            </AppFormField>
            {showVisibilitySection ? (
              <AppFieldGridSpan span="full">
                <SearchVisibilityPanel
                  value={form.visibility}
                  editable
                  showWorkspaceOption={showWorkspaceVisibilityOption}
                  onChange={(visibility) => onChange({ ...form, visibility })}
                />
              </AppFieldGridSpan>
            ) : null}
          </AppFieldGrid>
        </AppAccordion>

        {filterGroups.map(({ group, label, fields }) => (
          <AppAccordion
            key={group}
            id={GROUP_PANEL_IDS[group]}
            storageScope={storageScope}
            title={groupTitle(group, label)}
            defaultExpanded={GROUP_DEFAULT_EXPANDED[group] ?? false}
          >
            <AppFieldGrid columns={4}>{fields.map((field) => renderField(field, form, onChange))}</AppFieldGrid>
          </AppAccordion>
        ))}

        <AppAccordion
          id="sources"
          storageScope={storageScope}
          title="Sources"
          subtitle="Marketplaces to scrape"
          badge={form.source_names.length || undefined}
          defaultExpanded={false}
        >
          {sourceLimitHint ? <p className="muted">{sourceLimitHint}</p> : null}
          <SourceSelector
            sources={sources}
            selected={form.source_names}
            onToggle={onToggleSource}
            onSelectAll={onSelectAllSources}
            onClearAll={onClearAllSources}
            legend="Sources"
            hideLegend
            emptyHint={
              sources.length === 0 && !isAdmin ? 'No sources assigned. Contact admin.' : undefined
            }
            groupByCategory
          />
        </AppAccordion>

        {notificationFields.length > 0 && (
          <AppAccordion
            id="notification"
            storageScope={storageScope}
            title="Notification"
            subtitle="Optional message templates"
            defaultExpanded={false}
          >
            <AppFieldGrid columns={4}>
              {notificationFields.map((field) => renderField(field, form, onChange))}
            </AppFieldGrid>
          </AppAccordion>
        )}

        {editId && (
          <AppAccordion
            id="advanced"
            storageScope={storageScope}
            title="Advanced"
            subtitle="Raw filters preview"
            defaultExpanded={false}
          >
            <pre className="admin-json-preview">
              {JSON.stringify(
                Object.fromEntries(
                  categoryDef?.fields
                    .map((f) => [f.filterKey, form[f.key]])
                    .filter(([, v]) => v != null && v !== '') ?? [],
                ),
                null,
                2,
              )}
            </pre>
          </AppAccordion>
        )}

        <StickyFormBar>
          <AppButton type="submit" variant="primary">
            {editId ? 'Save changes' : 'Create search'}
          </AppButton>
          {editId && onCancelEdit ? (
            <AppButton type="button" variant="secondary" onClick={onCancelEdit}>
              Cancel
            </AppButton>
          ) : null}
        </StickyFormBar>
      </form>
    </AppAccordion>
  );
}
